/**
 * Anniversary Box Builder
 * Custom element powering the shared "Anniversary Box" picker used on all
 * three 10th Anniversary promo product pages (per_bottle / paid_free / pairs).
 * State is kept locally in memory and only sent to the cart when the CTA is
 * pressed, via /cart/update.js so re-visits and edits both work in one call.
 *
 * Self-customizable text (25 Sep): per_bottle mode's price line, status
 * messages and CTA labels are now driven by config.copy (block settings in
 * the theme customizer) via applyTemplate(), with the original English copy
 * as the fallback if a setting is left blank. paid_free/pairs are unchanged.
 */

class AnnivBoxBuilder extends HTMLElement {
  constructor() {
    super();
    this.state = new Map(); // variantId -> qty
    this.pairSelection = { buy: null, free: null };
  }

  connectedCallback() {
    const configEl = this.querySelector('[data-anniv-config]');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (err) {
      console.error('anniv-box-builder: invalid config JSON', err);
      return;
    }

    this.mode = this.config.mode;
    this.copy = this.config.copy || {};
    this.currencyFormatter = new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: this.config.currency || 'SGD',
    });

    this.config.variants.forEach((v) => {
      if (v.qty > 0) this.state.set(v.id, v.qty);
    });

    this.els = {
      priceLine: this.querySelector('[data-anniv-price-line]'),
      gridBottle: this.querySelector('[data-anniv-grid="bottle"]'),
      gridPaid: this.querySelector('[data-anniv-grid="paid"]'),
      gridFree: this.querySelector('[data-anniv-grid="free"]'),
      step2: this.querySelector('[data-anniv-step2]'),
      pickBuy: this.querySelector('[data-anniv-pick-buy]'),
      pickFree: this.querySelector('[data-anniv-pick-free]'),
      pairPreview: this.querySelector('[data-anniv-pair-preview]'),
      tray: this.querySelector('[data-anniv-tray]'),
      progress: this.querySelector('[data-anniv-progress]'),
      progressBar: this.querySelector('[data-anniv-progress-bar]'),
      status: this.querySelector('[data-anniv-status]'),
      statusBar: this.querySelector('[data-anniv-status-bar]'),
      total: this.querySelector('[data-anniv-total]'),
      totalBar: this.querySelector('[data-anniv-total-bar]'),
      cta: this.querySelector('[data-anniv-cta]'),
      ctaBar: this.querySelector('[data-anniv-cta-bar]'),
      error: this.querySelector('[data-anniv-error]'),
      mobileBar: this.querySelector('[data-anniv-mobile-bar]'),
      summary: this.querySelector('[data-anniv-summary]'),
      count: this.querySelector('[data-anniv-count]'),
      savings: this.querySelector('[data-anniv-savings]'),
      chosen: this.querySelector('[data-anniv-chosen]'),
    };

    this.renderPriceLine();

    if (this.mode === 'pairs') {
      this.buildPairMaps();
      this.renderPairPickers();
      this.renderTray();
    } else {
      this.renderGrids();
    }

    this.renderSummary();
    this.bindEvents();
    this.setupMobileBarObserver();
  }

  // ---------- helpers ----------

  money(cents) {
    return this.currencyFormatter.format(Number(cents) / 100);
  }

  moneyShort(cents) {
    return this.money(cents).replace(/\.00$/, '');
  }

  // Fills {token} placeholders in a copy template. Falls back to
  // fallbackStr if tpl is blank/missing (i.e. the merchant left the
  // customizer field empty).
  applyTemplate(tpl, fallbackStr, vars) {
    const str = tpl && String(tpl).trim() !== '' ? tpl : fallbackStr;
    return str.replace(/\{(\w+)\}/g, (m, key) => (key in vars ? vars[key] : m));
  }

  usualCents() {
    let cents = 0;
    this.state.forEach((qty, id) => {
      const v = this.config.variants.find((x) => x.id === id);
      if (v) cents += Number(v.compareAtPrice || v.price) * qty;
    });
    return cents;
  }

  formatNotes(notes) {
    return String(notes)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' &middot; ');
  }

  flavours() {
    const seen = new Set();
    const out = [];
    this.config.variants.forEach((v) => {
      if (!seen.has(v.flavour)) {
        seen.add(v.flavour);
        out.push(v.flavour);
      }
    });
    return out;
  }

  variantsFor(type) {
    if (!type) return this.config.variants;
    return this.config.variants.filter((v) => v.type === type);
  }

  totalBottleQty() {
    let n = 0;
    this.state.forEach((qty) => (n += qty));
    return n * (this.config.bottlesPerUnit || 1);
  }

  totalCents() {
    let cents = 0;
    this.state.forEach((qty, id) => {
      const v = this.config.variants.find((x) => x.id === id);
      if (v) cents += Number(v.price) * qty;
    });
    return cents;
  }

  // ---------- render: price line ----------

  renderPriceLine() {
    if (!this.els.priceLine) return;
    const v0 = this.config.variants[0];
    const min = this.config.minUnits || 10;
    let html = '';
    if (this.mode === 'per_bottle') {
      const wasCents = v0.compareAtPrice || v0.price * 2;
      const vars = {
        price: this.moneyShort(v0.price),
        was: this.moneyShort(wasCents),
        pair_price: this.moneyShort(v0.price * 2),
        pair_was: this.moneyShort(wasCents * 2),
        min: min,
        total: this.moneyShort(v0.price * min),
      };
      const main = this.applyTemplate(this.copy.priceMain, '{price} a bottle', vars);
      const was = this.applyTemplate(this.copy.priceWas, '{was}', vars);
      const badge = this.applyTemplate(this.copy.priceBadge, '1 for 1', vars);
      const sub = this.applyTemplate(this.copy.priceSub, '{min} bottles for {total}. Every 2nd bottle is free.', vars);
      html = `<span class="anniv-box-builder__price-now">${main}</span>
        <span class="anniv-box-builder__price-was">${was}</span>
        <span class="anniv-box-builder__chip">${badge}</span>
        <p class="anniv-box-builder__price-sub">${sub}</p>`;
    } else if (this.mode === 'paid_free') {
      const paid = this.variantsFor('Buy')[0] || v0;
      html = `<span class="anniv-box-builder__price-now">${this.money(paid.price)} each, then one free for every one you pay for</span>
        <p class="anniv-box-builder__price-sub">5 paid + 5 free = ${this.money(paid.price * 5)} (${this.money(paid.price / 2)} a bottle)</p>`;
    } else {
      html = `<span class="anniv-box-builder__price-now">${this.money(v0.price)} a pair (2 bottles)</span>
        <span class="anniv-box-builder__price-was">${this.money(v0.compareAtPrice || v0.price * 2)}</span>
        <p class="anniv-box-builder__price-sub">5 pairs = 10 bottles for ${this.money(v0.price * 5)}</p>`;
    }
    this.els.priceLine.innerHTML = html;
  }

  // ---------- render: card grids (per_bottle, paid_free) ----------

  cardMarkup(variant, extraClass) {
    const qty = this.state.get(variant.id) || 0;
    const img = variant.photo
      ? `<img src="${variant.photo}" alt="${variant.flavour}" loading="lazy" width="200" height="200">`
      : `<div class="anniv-box-builder__card-fallback">${variant.flavour}</div>`;
    const priceHtml =
      variant.type === 'Free'
        ? `<span class="anniv-box-builder__card-price"><s>${this.money(variant.compareAtPrice || variant.price)}</s> FREE</span>`
        : `<span class="anniv-box-builder__card-price">${this.money(variant.price)}${
            variant.compareAtPrice ? ` <s>${this.money(variant.compareAtPrice)}</s>` : ''
          }</span>`;

    return `
      <div class="anniv-box-builder__card${extraClass ? ' ' + extraClass : ''}${qty > 0 ? ' is-selected' : ''}" data-anniv-card data-variant-id="${variant.id}">
        <div class="anniv-box-builder__card-media">
          ${img}
          <span class="anniv-box-builder__badge" data-anniv-badge ${qty > 0 ? '' : 'hidden'}>x${qty}</span>
        </div>
        <p class="anniv-box-builder__card-name">${variant.flavour}</p>
        ${priceHtml}
        <div class="anniv-box-builder__card-action" data-anniv-card-action>
          ${
            qty > 0
              ? `<div class="anniv-box-builder__stepper">
                  <button type="button" data-anniv-dec aria-label="Remove one ${variant.flavour}">-</button>
                  <span data-anniv-qty>${qty}</span>
                  <button type="button" data-anniv-inc aria-label="Add one ${variant.flavour}">+</button>
                 </div>`
              : `<button type="button" class="anniv-box-builder__add" data-anniv-inc>+ Add</button>`
          }
        </div>
      </div>`;
  }

  rowMarkup(variant) {
    const qty = this.state.get(variant.id) || 0;
    const img = variant.photo
      ? `<img src="${variant.photo}" alt="" loading="lazy" width="150" height="150">`
      : `<svg class="anniv-box-builder__row-bottle" viewBox="0 0 40 64" aria-hidden="true"><rect x="13" y="3" width="14" height="8" rx="2"/><path d="M12 13h16c2.2 0 4 1.8 4 4v39c0 2.8-2.2 5-5 5H13c-2.8 0-5-2.2-5-5V17c0-2.2 1.8-4 4-4z"/></svg>`;
    const notes = variant.notes ? `<span class="anniv-box-builder__row-notes">${this.formatNotes(variant.notes)}</span>` : '';
    const soldOut = variant.available === false;
    return `
      <div class="anniv-box-builder__row${qty > 0 ? ' is-selected' : ''}${soldOut ? ' is-sold-out' : ''}" data-anniv-row data-variant-id="${variant.id}">
        <div class="anniv-box-builder__row-media">${img}</div>
        <div class="anniv-box-builder__row-text">
          <span class="anniv-box-builder__row-name">${variant.flavour}</span>
          ${soldOut ? '<span class="anniv-box-builder__row-notes">Sold out</span>' : notes}
        </div>
        <div class="anniv-box-builder__qty" role="group" aria-label="${variant.flavour} quantity">
          <button type="button" class="anniv-box-builder__qty-btn" data-anniv-dec aria-label="Remove one ${variant.flavour}" ${qty > 0 ? '' : 'disabled'}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 10h10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
          <span class="anniv-box-builder__qty-num" data-anniv-qty aria-live="polite">${qty}</span>
          <button type="button" class="anniv-box-builder__qty-btn anniv-box-builder__qty-btn--add" data-anniv-inc aria-label="Add one ${variant.flavour}" ${soldOut ? 'disabled' : ''}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 5v10M5 10h10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
      </div>`;
  }

  updateRow(id) {
    const row = this.querySelector(`[data-anniv-row][data-variant-id="${id}"]`);
    if (!row) return;
    const qty = this.state.get(id) || 0;
    const num = row.querySelector('[data-anniv-qty]');
    num.textContent = qty;
    num.classList.remove('is-bump');
    void num.offsetWidth;
    num.classList.add('is-bump');
    row.classList.toggle('is-selected', qty > 0);
    row.querySelector('[data-anniv-dec]').disabled = qty === 0;
  }

  renderGrids() {
    if (this.mode === 'per_bottle' && this.els.gridBottle) {
      this.els.gridBottle.innerHTML = this.variantsFor(null)
        .map((v) => this.rowMarkup(v))
        .join('');
    } else if (this.mode === 'paid_free') {
      if (this.els.gridPaid) {
        this.els.gridPaid.innerHTML = this.variantsFor('Buy')
          .map((v) => this.cardMarkup(v))
          .join('');
      }
      if (this.els.gridFree) {
        this.els.gridFree.innerHTML = this.variantsFor('Free')
          .map((v) => this.cardMarkup(v))
          .join('');
      }
      this.updateStep2Gate();
    }
  }

  updateStep2Gate() {
    if (this.mode !== 'paid_free' || !this.els.step2) return;
    const paidQty = this.paidQty();
    const gateOpen = paidQty >= (this.config.paidMin || 5);
    this.els.step2.classList.toggle('is-locked', !gateOpen);
    const label = this.querySelector('[data-anniv-step2-label]');
    if (label) {
      label.textContent = gateOpen
        ? `Step 2. Pick ${this.config.paidMin} on us`
        : 'Pick 5 you pay for first';
    }
  }

  paidQty() {
    let n = 0;
    this.variantsFor('Buy').forEach((v) => (n += this.state.get(v.id) || 0));
    return n;
  }

  freeQty() {
    let n = 0;
    this.variantsFor('Free').forEach((v) => (n += this.state.get(v.id) || 0));
    return n;
  }

  // ---------- render: pairs mode ----------

  buildPairMaps() {
    // variant.flavour = paid flavour (option1), variant.type = free flavour (option2)
    this.pairMap = new Map();
    this.config.variants.forEach((v) => {
      this.pairMap.set(`${v.flavour}|${v.type}`, v);
    });
  }

  renderPairPickers() {
    const flavours = this.flavours();
    const pickerCard = (flavour, group) => {
      const selected = this.pairSelection[group] === flavour;
      const variant = this.config.variants.find((v) => v.flavour === flavour);
      const img = variant && variant.photo
        ? `<img src="${variant.photo}" alt="${flavour}" loading="lazy" width="160" height="160">`
        : `<div class="anniv-box-builder__card-fallback">${flavour}</div>`;
      return `<button type="button" class="anniv-box-builder__pick${selected ? ' is-selected' : ''}"
        data-anniv-pick="${group}" data-flavour="${flavour}">
        <div class="anniv-box-builder__card-media">${img}</div>
        <span class="anniv-box-builder__card-name">${flavour}</span>
      </button>`;
    };

    if (this.els.pickBuy) {
      this.els.pickBuy.innerHTML = flavours.map((f) => pickerCard(f, 'buy')).join('');
    }
    if (this.els.pickFree) {
      this.els.pickFree.innerHTML = flavours.map((f) => pickerCard(f, 'free')).join('');
    }
    this.renderPairPreview();
  }

  renderPairPreview() {
    if (!this.els.pairPreview) return;
    const { buy, free } = this.pairSelection;
    if (!buy) {
      this.els.pairPreview.hidden = true;
      return;
    }
    const freeFlavour = free || buy; // defaults to same flavour
    const variant = this.pairMap.get(`${buy}|${freeFlavour}`);
    if (!variant) {
      this.els.pairPreview.hidden = true;
      return;
    }
    this.els.pairPreview.hidden = false;
    this.els.pairPreview.innerHTML = `
      <p class="anniv-box-builder__pair-line">${buy} + ${freeFlavour} (free) &middot; ${this.money(variant.price)}</p>
      <button type="button" class="anniv-box-builder__add" data-anniv-add-pair data-variant-id="${variant.id}">Add pair to box</button>
    `;
  }

  renderTray() {
    if (!this.els.tray) return;
    const rows = [];
    this.state.forEach((qty, id) => {
      const v = this.config.variants.find((x) => x.id === id);
      if (v && qty > 0) rows.push({ v, qty });
    });
    if (!rows.length) {
      this.els.tray.innerHTML = '<p class="anniv-box-builder__tray-empty">No pairs added yet.</p>';
      return;
    }
    this.els.tray.innerHTML = rows
      .map(
        ({ v, qty }) => `
      <div class="anniv-box-builder__tray-row" data-variant-id="${v.id}">
        <span>${v.flavour} + ${v.type} (free)</span>
        <span class="anniv-box-builder__stepper">
          <button type="button" data-anniv-dec aria-label="Remove one pair">-</button>
          <span data-anniv-qty>x${qty}</span>
          <button type="button" data-anniv-inc aria-label="Add one pair">+</button>
        </span>
      </div>`
      )
      .join('');
  }

  // ---------- summary / status / progress ----------

  deriveState() {
    const mode = this.mode;
    let valid = false;
    let status = '';
    let ctaLabel = 'Add to cart';
    let slotsFilled = 0;
    let slotsTotal = 10;

    if (mode === 'per_bottle') {
      const n = this.totalBottleQty();
      const min = this.config.minUnits || 10;
      slotsTotal = min;
      slotsFilled = Math.min(n, min);
      const ctaReady = this.applyTemplate(this.copy.ctaReady, 'Add box to cart', {});
      if (n === 0) {
        status = this.applyTemplate(this.copy.statusEmpty, 'Pick at least {min} bottles, any mix of flavours.', { min: min });
        ctaLabel = this.applyTemplate(this.copy.ctaBelowMin, 'Choose {remaining} more bottles', { remaining: min });
      } else if (n < min) {
        status = this.applyTemplate(this.copy.statusBelowMin, '{remaining} more to unlock your 1 for 1 box.', {
          n: n,
          min: min,
          remaining: min - n,
        });
        ctaLabel = this.applyTemplate(this.copy.ctaBelowMin, 'Choose {remaining} more bottles', { remaining: min - n });
      } else if (this.config.requireEven && n % 2 !== 0) {
        status = this.applyTemplate(this.copy.statusOdd, 'Add 1 more so every bottle has its pair.', { n: n });
        ctaLabel = this.applyTemplate(this.copy.ctaOdd, 'Add 1 more', {});
      } else {
        valid = true;
        status = this.applyTemplate(this.copy.statusValid, 'Box unlocked. {free} of your {n} bottles are on us.', {
          n: n,
          free: Math.floor(n / 2),
        });
        ctaLabel = ctaReady;
      }
    } else if (mode === 'paid_free') {
      const paid = this.paidQty();
      const free = this.freeQty();
      const min = this.config.paidMin || 5;
      slotsTotal = min * 2;
      slotsFilled = Math.min(paid + free, slotsTotal);
      if (paid < min) {
        status = `Paid ${paid}/${min} &middot; Free ${free}/${Math.max(paid, min)}`;
        ctaLabel = `Add ${min - paid} more paid`;
      } else if (free > paid) {
        status = `Your free bottles need a matching bottle you pay for.`;
        ctaLabel = `Remove ${free - paid} free`;
      } else if (free < paid) {
        status = `Paid ${paid} &middot; Free ${free}/${paid}`;
        ctaLabel = `Add ${paid - free} more free`;
      } else {
        valid = true;
        status = `Box ready: ${paid} paid + ${free} free`;
      }
    } else {
      // pairs
      let pairCount = 0;
      this.state.forEach((qty) => (pairCount += qty));
      const min = this.config.pairsMin || 5;
      slotsTotal = min;
      slotsFilled = Math.min(pairCount, min);
      if (pairCount === 0) {
        status = `Pick ${min} pairs to start your box.`;
      } else if (pairCount < min) {
        status = `${pairCount} of ${min} pairs (= ${pairCount * 2} bottles)`;
        ctaLabel = `Add ${min - pairCount} more pairs`;
      } else {
        valid = true;
        status = `${pairCount} of ${min} pairs (= ${pairCount * 2} bottles)`;
      }
    }

    return { valid, status, ctaLabel, slotsFilled, slotsTotal };
  }

  renderSummary() {
    const d = this.deriveState();
    this.lastValid = d.valid;

    const pct = d.slotsTotal ? Math.min(100, Math.round((d.slotsFilled / d.slotsTotal) * 100)) : 0;
    [this.els.progress, this.els.progressBar].forEach((el) => {
      if (!el) return;
      el.style.width = pct + '%';
      el.classList.toggle('is-complete', d.valid);
    });
    this.classList.toggle('is-valid', d.valid);

    if (this.els.status) this.els.status.innerHTML = d.status;

    if (this.mode === 'per_bottle') {
      const n = this.totalBottleQty();
      const min = this.config.minUnits || 10;
      const countText = n >= min ? `${n} bottles` : `${n} / ${min} bottles`;
      if (this.els.count) this.els.count.textContent = countText;
      if (this.els.statusBar) {
        this.els.statusBar.innerHTML = d.valid ? `${n} bottles &middot; ${Math.floor(n / 2)} free` : countText;
      }
      this.renderSavings();
      this.renderChosen();
    } else if (this.els.statusBar) {
      this.els.statusBar.innerHTML = d.status;
    }

    const totalText = this.money(this.totalCents());
    if (this.els.total) this.els.total.textContent = totalText;
    if (this.els.totalBar) this.els.totalBar.textContent = totalText;

    const ctaText = d.valid ? `${d.ctaLabel} &middot; ${totalText}` : d.ctaLabel;
    [this.els.cta, this.els.ctaBar].forEach((btn) => {
      if (!btn) return;
      btn.innerHTML = ctaText;
      btn.disabled = !d.valid;
    });
  }

  renderSavings() {
    if (!this.els.savings) return;
    const paid = this.totalCents();
    const usual = this.usualCents();
    if (!paid || usual <= paid) {
      this.els.savings.hidden = true;
      return;
    }
    this.els.savings.hidden = false;
    this.els.savings.innerHTML = `<s>${this.money(usual)}</s> <span>You save ${this.money(usual - paid)}</span>`;
  }

  renderChosen() {
    if (!this.els.chosen) return;
    const items = [];
    this.config.variants.forEach((v) => {
      const qty = this.state.get(v.id) || 0;
      if (qty > 0) items.push(`<li>${v.flavour} <b>&times;${qty}</b></li>`);
    });
    this.els.chosen.hidden = items.length === 0;
    this.els.chosen.innerHTML = items.join('');
  }

  // ---------- events ----------

  bindEvents() {
    this.addEventListener('click', (e) => {
      const card = e.target.closest('[data-anniv-card]');
      const incBtn = e.target.closest('[data-anniv-inc]');
      const decBtn = e.target.closest('[data-anniv-dec]');
      const pickBtn = e.target.closest('[data-anniv-pick]');
      const addPairBtn = e.target.closest('[data-anniv-add-pair]');
      const trayRow = e.target.closest('[data-anniv-tray] [data-variant-id]');

      if (pickBtn) {
        const group = pickBtn.getAttribute('data-anniv-pick');
        const flavour = pickBtn.dataset.flavour;
        this.pairSelection[group] = flavour;
        if (group === 'buy' && !this.pairSelection.free) {
          this.pairSelection.free = flavour; // default free = same flavour
        }
        this.renderPairPickers();
        return;
      }

      if (addPairBtn) {
        const id = Number(addPairBtn.dataset.variantId);
        this.state.set(id, (this.state.get(id) || 0) + 1);
        this.renderTray();
        this.renderSummary();
        return;
      }

      if (trayRow) {
        const id = Number(trayRow.dataset.variantId);
        if (incBtn) this.state.set(id, (this.state.get(id) || 0) + 1);
        if (decBtn) {
          const next = (this.state.get(id) || 0) - 1;
          if (next <= 0) this.state.delete(id);
          else this.state.set(id, next);
        }
        this.renderTray();
        this.renderSummary();
        return;
      }

      const row = e.target.closest('[data-anniv-row]');
      if (row && (incBtn || decBtn)) {
        const id = Number(row.dataset.variantId);
        if (incBtn && !incBtn.disabled) this.state.set(id, (this.state.get(id) || 0) + 1);
        if (decBtn && !decBtn.disabled) {
          const next = (this.state.get(id) || 0) - 1;
          if (next <= 0) this.state.delete(id);
          else this.state.set(id, next);
        }
        this.updateRow(id);
        this.renderSummary();
        return;
      }

      if (card && (incBtn || decBtn)) {
        const id = Number(card.dataset.variantId);
        if (incBtn) this.state.set(id, (this.state.get(id) || 0) + 1);
        if (decBtn) {
          const next = (this.state.get(id) || 0) - 1;
          if (next <= 0) this.state.delete(id);
          else this.state.set(id, next);
        }
        if (this.mode === 'pairs') {
          this.renderTray();
        } else {
          this.renderGrids();
        }
        this.renderSummary();
        return;
      }

      if (e.target.closest('[data-anniv-cta]') || e.target.closest('[data-anniv-cta-bar]')) {
        this.submit();
      }
    });
  }

  setupMobileBarObserver() {
    if (!this.els.mobileBar || !this.els.summary) return;
    if (typeof IntersectionObserver === 'undefined') {
      this.els.mobileBar.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.els.mobileBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(this.els.summary);
  }

  // ---------- submit ----------

  async submit() {
    if (!this.lastValid) return;
    if (this.els.error) {
      this.els.error.hidden = true;
      this.els.error.textContent = '';
    }
    [this.els.cta, this.els.ctaBar].forEach((btn) => btn && (btn.disabled = true));

    const updates = {};
    this.config.variants.forEach((v) => {
      updates[v.id] = this.state.get(v.id) || 0;
    });

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.description || data.message || 'Could not update your box.');
      }
      document.dispatchEvent(
        new CustomEvent('cart:update', { bubbles: true, detail: { source: 'anniv-box-builder' } })
      );
      window.location.href = '/cart';
    } catch (err) {
      if (this.els.error) {
        this.els.error.hidden = false;
        this.els.error.textContent = err.message || 'Something went wrong adding your box. Please try again.';
      }
      [this.els.cta, this.els.ctaBar].forEach((btn) => btn && (btn.disabled = !this.lastValid));
    }
  }
}

if (!customElements.get('anniv-box-builder')) {
  customElements.define('anniv-box-builder', AnnivBoxBuilder);
}

// Auto-upgrade the plain wrapper div into the custom element's behaviour
// without requiring a custom tag name change in the Liquid block markup.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-anniv-box-builder]').forEach((el) => {
    if (el.tagName !== 'ANNIV-BOX-BUILDER') {
      const wrapper = document.createElement('anniv-box-builder');
      wrapper.innerHTML = el.innerHTML;
      Array.from(el.attributes).forEach((attr) => wrapper.setAttribute(attr.name, attr.value));
      el.replaceWith(wrapper);
    }
  });
});
